/**
 * OmegaLaunchPlansInsightsPage — Phase 26 analytics tab.
 *
 * Client-side analytics computed from the full unfiltered plan set.
 * Tabs: Domain breakdown · Status funnel · Confidence histogram · Top plans by revenue
 */

import { useState } from 'react';
import { useLaunchPlans } from '../hooks/useOmegaState';
import type { LaunchPlanSummary } from '../data/types';

function fmt(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

function founderScore(p: LaunchPlanSummary): number {
  return (
    p.confidence * 0.5 +
    Math.min(p.growth_rate_yoy, 1.0) * 0.3 +
    Math.min(Math.log10(p.market_size_usd + 1) / 12, 1) * 0.2
  );
}

// Simple horizontal bar
function Bar({ label, value, max, color }: { label: string; value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '8.5pt' }}>
      <div style={{ width: 130, color: 'var(--omega-ink-subtle)', textAlign: 'right', flexShrink: 0 }}>
        {label}
      </div>
      <div
        style={{
          flex: 1,
          height: 10,
          background: 'rgba(255,255,255,0.07)',
          borderRadius: 5,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: color ?? 'var(--omega-green)',
            borderRadius: 5,
            transition: 'width 0.4s',
          }}
        />
      </div>
      <div style={{ width: 28, color: 'var(--omega-ink)', fontWeight: 600, flexShrink: 0 }}>
        {value}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--omega-surface-2, rgba(255,255,255,0.04))',
        border: '1px solid var(--omega-border, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          fontSize: '8.5pt',
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: 'var(--omega-ink-subtle)',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

export function OmegaLaunchPlansInsightsPage() {
  const { data, isPending, isError } = useLaunchPlans();
  const [topTab, setTopTab] = useState<'revenue' | 'market' | 'score'>('score');

  if (isPending) {
    return (
      <div style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink-subtle)', padding: 24, fontSize: '9pt' }}>
        Loading…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-red, #f87171)', padding: 24, fontSize: '9pt' }}>
        Failed to load plan data.
      </div>
    );
  }

  const plans = data.plans;

  // Domain breakdown
  const byDomain = new Map<string, number>();
  for (const p of plans) {
    byDomain.set(p.domain, (byDomain.get(p.domain) ?? 0) + 1);
  }
  const domainEntries = Array.from(byDomain.entries()).sort((a, b) => b[1] - a[1]).slice(0, 15);
  const maxDomain = domainEntries[0]?.[1] ?? 1;

  // Status funnel
  const byStatus = new Map<string, number>();
  for (const p of plans) {
    byStatus.set(p.status, (byStatus.get(p.status) ?? 0) + 1);
  }
  const statusOrder = ['draft', 'validated', 'approved', 'deferred', 'rejected'];
  const statusColors: Record<string, string> = {
    validated: 'var(--omega-green)',
    approved: '#6ee7b7',
    draft: 'rgba(255,255,255,0.35)',
    rejected: 'var(--omega-red, #f87171)',
    deferred: '#fbbf24',
  };

  // Confidence histogram (buckets: 0-25, 25-50, 50-75, 75-100)
  const buckets = [
    { label: '0–25%', count: plans.filter(p => p.confidence < 0.25).length, color: '#f87171' },
    { label: '25–50%', count: plans.filter(p => p.confidence >= 0.25 && p.confidence < 0.5).length, color: '#fbbf24' },
    { label: '50–75%', count: plans.filter(p => p.confidence >= 0.5 && p.confidence < 0.75).length, color: '#60a5fa' },
    { label: '75–100%', count: plans.filter(p => p.confidence >= 0.75).length, color: 'var(--omega-green)' },
  ];
  const maxBucket = Math.max(...buckets.map(b => b.count), 1);

  // Top plans
  const topPlans = [...plans].sort((a, b) => {
    if (topTab === 'score') return founderScore(b) - founderScore(a);
    if (topTab === 'revenue') return b.revenue_projection.year1 - a.revenue_projection.year1;
    return b.market_size_usd - a.market_size_usd;
  }).slice(0, 10);

  // Summary stats
  const avgConfidence = plans.length > 0
    ? plans.reduce((s, p) => s + p.confidence, 0) / plans.length
    : 0;
  const totalY1Rev = plans.reduce((s, p) => s + p.revenue_projection.year1, 0);
  const approvedCount = byStatus.get('approved') ?? 0;
  const plansNeedingAtoms = plans.filter(p => p.required_new_atoms.length > 0).length;

  return (
    <div
      style={{
        fontFamily: 'var(--omega-mono)',
        color: 'var(--omega-ink)',
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: 960,
      }}
    >
      {/* Page title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <span style={{ fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Launch Plans — Insights
        </span>
        <span
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid var(--omega-border, rgba(255,255,255,0.1))',
            borderRadius: 3,
            fontSize: '8pt',
            padding: '1px 7px',
            color: 'var(--omega-ink-subtle)',
          }}
        >
          {plans.length} plans
        </span>
      </div>

      {/* Summary stat tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 10,
        }}
      >
        {[
          { label: 'Total plans', value: String(plans.length) },
          { label: 'Avg confidence', value: `${Math.round(avgConfidence * 100)}%` },
          { label: 'Total Y1 pipeline', value: fmt(totalY1Rev) },
          { label: 'Approved', value: String(approvedCount) },
          { label: 'Need new atoms', value: String(plansNeedingAtoms) },
        ].map(({ label, value }) => (
          <div
            key={label}
            style={{
              background: 'var(--omega-surface-2, rgba(255,255,255,0.04))',
              border: '1px solid var(--omega-border, rgba(255,255,255,0.08))',
              borderRadius: 6,
              padding: '10px 14px',
            }}
          >
            <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ fontSize: '14pt', fontWeight: 700, color: 'var(--omega-green)' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Domain breakdown */}
        <SectionCard title="Plans by domain">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {domainEntries.map(([domain, count]) => (
              <Bar key={domain} label={domain} value={count} max={maxDomain} />
            ))}
          </div>
        </SectionCard>

        {/* Status funnel */}
        <SectionCard title="Status funnel">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {statusOrder.map(s => (
              <Bar
                key={s}
                label={s}
                value={byStatus.get(s) ?? 0}
                max={plans.length || 1}
                color={statusColors[s] ?? 'rgba(255,255,255,0.25)'}
              />
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Confidence histogram */}
      <SectionCard title="Confidence distribution">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {buckets.map(b => (
            <Bar key={b.label} label={b.label} value={b.count} max={maxBucket} color={b.color} />
          ))}
        </div>
      </SectionCard>

      {/* Top plans table */}
      <SectionCard title="Top plans">
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {(['score', 'revenue', 'market'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTopTab(t)}
              style={{
                background: topTab === t ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
                border: '1px solid var(--omega-border, rgba(255,255,255,0.1))',
                borderRadius: 4,
                color: topTab === t ? 'var(--omega-ink)' : 'var(--omega-ink-subtle)',
                fontFamily: 'var(--omega-mono)',
                fontSize: '8pt',
                padding: '3px 10px',
                cursor: 'pointer',
                fontWeight: topTab === t ? 700 : 400,
              }}
            >
              {t === 'score' ? 'Founder score' : t === 'revenue' ? 'Y1 revenue' : 'Market size'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr',
              fontSize: '7.5pt',
              color: 'var(--omega-ink-subtle)',
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              padding: '4px 0',
              borderBottom: '1px solid var(--omega-border, rgba(255,255,255,0.08))',
              marginBottom: 4,
            }}
          >
            <span>Product</span>
            <span>Y1 Revenue</span>
            <span>Confidence</span>
            <span>Score</span>
          </div>
          {topPlans.map(p => (
            <div
              key={p.plan_id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                fontSize: '8.5pt',
                padding: '5px 0',
                borderBottom: '1px solid var(--omega-border, rgba(255,255,255,0.05))',
                alignItems: 'center',
              }}
            >
              <div>
                <span style={{ color: 'var(--omega-ink)', fontWeight: 600 }}>{p.product_name}</span>
                <span style={{ color: 'var(--omega-ink-subtle)', marginLeft: 6, fontSize: '7.5pt' }}>
                  {p.domain}
                </span>
              </div>
              <span style={{ color: 'var(--omega-green)' }}>{fmt(p.revenue_projection.year1)}</span>
              <span style={{ color: 'var(--omega-ink)' }}>{Math.round(p.confidence * 100)}%</span>
              <span style={{ color: 'var(--omega-ink)' }}>{Math.round(founderScore(p) * 100)}</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
